import { fn, INamed } from '../utils';
import isSet = fn.isSet;
import { IPageProperty, IPageMetaData, NavDtFormat, TDefaultPageObj, ILookupInfo, TPagePropRefs } from '../nav/navtypes';
import { Model, Observable, ObservableArray, IObservableArray, GridColumn } from '../nav/navmodel';
import { NavClientService } from './navclientservice';
import { INavPageClientInfo_Data, IPageQueryParameters } from './navdata';
import { IPageQueryConstraint, TPagePropertyAccess } from '../nav/navtypes';
import { NavPropertyItem } from './navpropertyitem';
import { Bind } from '../navkendo/kutils';
export { Model, IObservableArray, ObservableArray, Observable, GridColumn }

/** Cancellable Event on Execute Update on PageItem (triggered on attached Model)*/
export const $PageItemExecuteUpate = "executeUpdate";
/** Event on Update Executed */
export const $PageItemExecutedUpdate = "executedUpdate";

/** transfer JSON  deserialized Objects Props in dest object */
function transfer<TDest, TSource>(dest: TDest, source: TSource) {
    for (var name in source)
        (<any>dest)[name] = source[name];
    return <TDest & TSource>dest;
}

/** Page Client Info from Server is JSON Object, internally we use a "real" object  */
export class NavPageClientInfo<TPage = TDefaultPageObj> /* implements INavPageClientInfo_Data */ {
    constructor(_data: INavPageClientInfo_Data<TPage>, public readonly Service: NavClientService, public readonly PageName: string, public readonly NavUrl: string) {
        let me = transfer(this, _data);
        var meta = me.MetaData = <IPageMetaData<TPage>>new NavPageMetaData(me.MetaData);
        meta.Properties.forEach(p => {
            if (p.CustomSearch) {
                this._customSearch[p.Name] = p.CustomSearch;
            }
            if (arrProps = p.ArrayProperties) {
                var arrProps = p.ArrayProperties = <IPageMetaData>new NavPageMetaData(arrProps);
                arrProps.Parent = meta;
                arrProps.ParentProperty = p;
                // Enforce "Key" as KeyField if not explicitly  defined
                // (not a good idea if we Edit Line Item in grid! )
                if (!arrProps.KeyFields || arrProps.KeyFields.length == 0)
                    arrProps.KeyFields = ["Key"];
            }
        });

        let allMeths = this.AllMethods = <INamed<boolean>>{};
        [].concat(me.ObjectMethods || []).concat(me.RowMethods || []).forEach(item => allMeths[item] = true);
    }

    //#region Client Side Custom Functionality

    /** Container of All Properties by their Path , attached  on Client after Retrieval! */
    readonly AllProperties: INamed<IPageProperty> = {};
    /** Container for Available Properties = true (ObjectMethods + RowMethods) */
    readonly AllMethods: INamed<boolean>;

    /** Resolve Search Optimization */
    public ResolveSearchOptimization(constraintInfo: IPageQueryParameters<TPage> & { NoResult?: boolean }): PromiseLike<IPageQueryParameters<TPage> & { NoResult?: boolean }> {
        const $c = this.Service.$c,
            allConstraints = constraintInfo && constraintInfo.Constraints,
            maxRead = 200,
            customConstraints = allConstraints && allConstraints.filter(item => this._customSearch[item.FieldName]);
        if (!customConstraints || customConstraints.length == 0) {
            return $c.when(constraintInfo);
        }
        const customConstraintsInfo = customConstraints.map(customConstraint => {
            var c = this._customSearch[customConstraint.FieldName];
            var mappedProp = this.AllProperties[c.LookupField];
            if (!(mappedProp && mappedProp.HasLookup))
                throw new Error("Fehlerhafte Konfiguration für optimierte Suchkonfiguration :" + ((mappedProp && mappedProp.Name) || (customConstraint.FieldName)));
            if (!c.LookupInfo) {
                c.LookupInfo = this.Service.GetStaticLookupInfo(this.NavUrl, this.PageName, mappedProp.Name);
            }
            return {
                Constraint: customConstraint,
                CustomSearch: c,
                MappedProp: mappedProp
            };
        });
        var customConstraintGroups = fn.DictToArray(fn.GroupBy(customConstraintsInfo, info => info.MappedProp.Name));

        const resolve = customConstraintGroups.map(constraintGroup => {
            const firstItem = constraintGroup[0];
            const lookupInfo = firstItem.CustomSearch.LookupInfo;
            return lookupInfo.then(d => {
                return this.Service.ReadMultiple({
                    PageName: d.TargetPageName,
                    PageCount: maxRead + 1,
                    Properties: [d.TargetField],
                    Constraints: constraintGroup.map(item => <IPageQueryConstraint>{
                        FieldName: item.CustomSearch.LookupProperty,
                        Op: item.Constraint.Op,
                        Value: item.Constraint.Value
                    })
                }, this.NavUrl).then(r => {
                    if (r.Data.length > maxRead) {
                        const currTagetField = this.AllProperties[firstItem.MappedProp.Name].Title;
                        throw new Error(`Bitte schränken Sie Ihre Abfrage weiter ein: Die Suche nach '${currTagetField}(${ constraintGroup.map(item => item.Constraint.FieldName).join(",") })' liefert mehr als '${maxRead}' Ergebnisse!`);
                    }
                    if (r.Data.length == 0) {
                        // no Result : add. Search not necessary anymore
                        constraintInfo.NoResult = true;
                    }
                    constraintGroup.forEach((item, currGroupIdx) => {
                        let idx = allConstraints.indexOf(item.Constraint);

                        var filterValue = r.Data.length > 0 ? r.Data.map(el => el[d.TargetField]).filter(isSet).join("|") : "<!NORESULT!>";
                        // only replace first, remove others
                        if (currGroupIdx == 0) {
                            allConstraints.splice(idx, 1, { FieldName: <any>item.CustomSearch.LookupField, Value: filterValue });
                        } else {
                            allConstraints.splice(idx, 1);
                        }
                    });
                });
            });
        });
        return $c.all(resolve).then(() => $c.when(constraintInfo));
    }

    private readonly _customSearch: INamed<{
        LookupField: string,
        LookupProperty: string,
        LookupInfo?: PromiseLike<ILookupInfo>;
    }> = <any>{};
    //#endregion
}
export type INavPageClientInfo<TPage = TDefaultPageObj, TPageName extends string = string> = NavPageClientInfo<TPage> & INavPageClientInfo_Data<TPage, TPageName>;

/** Page Client Info from Server is JSON Object, internally we use a "real" object
* so we can use it on kendo options object (as gets cloned internally will fail otherwise)
 */
export class NavPageMetaData<TPage = any> extends Observable /** implements _IPageMetaData<TPage> */ {
    constructor(meta: IPageMetaData<TPage>) {
        super();
        transfer(this, meta);
    }

    /** Global Register Property Changed Callback for any Instantiated Property Item from given Metadata Description */
    public RegisterPropertyChanged(this: IPageMetaData<TPage>, propRef: TPagePropertyAccess<TPage> | Array<TPagePropertyAccess<TPage>> | "*",
        callback: (ev: { Prop?: IPageProperty; Item: NavPropertyItem<TPage> }) => void) {
        return Bind(this, e => {
             const item = e.Item as NavPropertyItem;
             item.RegisterPropertyChanged(propRef, callback);
             callback({ Item: item });
        }, "Instantiate");
    }

    /** Register Custom Enum Invalidation  depending on other props */
    RegisterInvalidateEnum<TPage>(this: IPageMetaData<TPage>,
        sourceProp: TPagePropRefs<TPage>,
        targetPropertyName: keyof TPage, invalid: (model: TPage) => number[], reverse?: boolean) {
        const targetProp: IPageProperty = this.PropsByName[targetPropertyName];
        const enumValues = targetProp?.EnumMappings;
        if (enumValues) {
            return this.RegisterPropertyChanged(sourceProp, e => {
                const Item = e.Item;
                if (reverse) {
                    const all = enumValues.map(item => item.Value);
                    setInvalid = () => {
                        const valid = invalid(Item.Model) || [];
                        return all.filter(item => valid.indexOf(item) < 0);
                    };

                } else {
                    var setInvalid = () => invalid(Item.Model);
                }
                Item.GetCustomInvalidEnumData(targetProp).SetInvalid(setInvalid());
            });
        }
    }

    Instantiate(this: IPageMetaData<TPage>, item: NavPropertyItem<TPage>) {
        this.trigger("Instantiate", { Item: item });
    }
}

/** Default behaviour for failure of retrieving Props By name from MetaData, true : error, false : silently ignore them */
export const __ValidateProps = false;

/** Create Kendo Model from IPageProperty Fields Container */
export function CreateKendoModel(props: IPageProperty[]) {
    const fields = props.map(p => {
        if (p.ArrayProperties) {
            var arrType = getOrCreateKendoModel(p.ArrayProperties);
            var explicitParse = fn.identity;
        } else {
            var simpleType = getkendoModelType(p);
        }
        const result = <kendo.data.DataSourceSchemaModelField>{
            field: p.Name,
            editable: !p.ReadOnly,
            type: arrType || simpleType,
            nullable: true
        };

        if (explicitParse) {
            // hierarchical model parse not supported, this scenario leads to errors otherwise
            result.parse = explicitParse;
        }
        return result;
    });
    fields["fields"] = fields; // fields : TODO : if Set we might have to adjust filter (...)

    return Model.define(<any>fields);
}

/** Determine the kendo Model Type by MetaData Info (and attach single instance to Item) */
export function getOrCreateKendoModel(meta: IPageMetaData) {
    var kTypeAcess: { _kType: typeof Model } = <any>meta;
    return kTypeAcess._kType ? kTypeAcess._kType : (kTypeAcess._kType = CreateKendoModel(meta.Properties));
}

/** Get or Create Client Key determination func for given MetaData Instance */
export function getClientKeyFunc(metaData: IPageMetaData): (data: any, noError?: boolean) => string {

    /** Get a string Representation of a Key Identifier from Explicit Key Fields (unique for all Root/Line Items) */
    function getClientKey(this: IPageMetaData, data: any, noError?: boolean): string {
        if (!data) {
            if (noError) return "";
            throw new Error("Expected data");
        }
        data = data.Model || data;
        var keys = this.KeyFields;
        if (!keys || keys.length == 0) {
            if (noError) return "";
            throw new Error("KeyFields not defined for '" + this.PageName + "'" + (this.PropertyName || ""));
        }

        return keys.map(item => {
            var keyValue = data[item];
            if (keyValue == undefined) {
                if (noError) return "";
                throw new Error("Key Value for '" + item + "' not defined on item for MetaData :" + this.PageName + (this.PropertyName ? "[" + this.PropertyName + "]" : ""));
            }
            return item + ":" + keyValue;
        }).join("#") + "#" + this.PageName + "#" + (this.PropertyName || "");
    }

    return metaData["GetClientKey"] || (metaData["GetClientKey"] = getClientKey.bind(metaData));
}

/** set Value on Model (TODO : Do we want to Use kendo Model?) */
export function setModel(model: Model, name: string, value: any) {
    model[name] = value;
    //Remark : standard "kendo" Model "change notification" functionality via "set"
    // - all our custom controls do not need this functionality
    // - LineDisplay, i.e. Grid use to be handled via this, but :
    //      -  Change refreshes whole line then --> poor performance
    // --> so Handle Change  notification in Grid Display Explicitly, avoid setting via Notification ....

    // TODO : Invalidates Grid Data!, Set via function is necessary for Grid Display to refresh
    //model.set(name, value);
}

/** get Value on Model (TODO : Do we want to Use kendo Model?) */
export function getModel<TModel extends kendo.data.ObservableObject, TValue extends keyof TModel>(model: TModel, name: TValue): TModel[TValue] {
    return model[name];
}

/** Check if a Change occured on prepared Values for given Property */
export function valHasChanged(prop: IPageProperty, val1: any, val2: any) {
    switch (prop.Type) {
        case "DateTime":
            if (val1 instanceof Date && val2 instanceof Date) {
                var dt1 = (<Date>val1).valueOf();
                var dt2 = (<Date>val2).valueOf();
                if (dt1 == dt2)
                    return false;
                // > 1000 : DateTime gets transferred as string, with a more exact precision thang JScript Date can handle
                // Negation:  imp Handle nan
                return (isNaN(dt1) != isNaN(dt2)) || (Math.abs(dt1 - dt2) > 1000);
            } else {
                val1 = val1 === null ? new Date(NaN) : val1;
                val2 = val2 === null ? new Date(NaN) : val2;
                return (new Date(val1)).toString() != new Date(val2).toString();
            }
        default:
            if (prop.ArrayProperties) {
                // TODO (necessary?)
                return false;
                //prop.ArrayProperties.Properties.some(v =>
            }
            else {
                if (val1 === val2)
                    return false;
                return val1 != val2;
            }
    }
}

/** get Kendo Model Type by PageProperty Definition */
function getkendoModelType(p: IPageProperty): any {
    switch (p.Type) {
        case "DateTime":
            return p.DateFormat == NavDtFormat.Time ? "time" : "date";
        case "Decimal":
        case "Int32":
            return "number";
        case "Boolean":
            return "boolean";
        default:
            if (p.ArrayProperties)
                return getOrCreateKendoModel(p.ArrayProperties);
            else if (p.EnumMappings)
                return "number";
            else
                return "string";
    }
}

/** Enforce Typed for given Property by Value */
export function enforceTyped(prop: IPageProperty, value: any) {
  var origValue = value;
  var fail = (message: string) => { throw new Error("Error Setting Property '" + prop.Name + " from '" + origValue + "'': " + message); };
  switch (prop.Type) {
    case "String":
      if (typeof value != "string" && value != null && value != undefined)
        fail("Value must be string");
      return value;
    case "Int32":
      if (value == null || value == undefined)
        return value;
      value = (value === "") ? 0 : parseInt(value);
      if (isNaN(value))
        fail("Value must be int");
      return value;
    case "Decimal":
      if (value == null || value == undefined)
        return value;
      if (typeof value == "string") { // parsefloat is not locale aware!
        value = value.replace(/\./g, "").replace(",", ".");
      }
      value = value === "" ? 0.0 : parseFloat(value);
      if (isNaN(value))
        fail("Value must be float");
      return value;
    case "Boolean":
      if (fn.isObject(value))
        fail("Set from Object not possible!");
      if (value == "true")
        value = true;
      if (value == "false")
        value = false;
      return !!value;

    case "DateTime":
      if (value instanceof Date) {
        if (isNaN(value.valueOf()))
          return null;

        if (prop.DateFormat == NavDtFormat.Time) {
          // for "Time" props that's the Date used in NAV (1.1.0001)
          value.setDate(1);
          value.setMonth(0);
          value.setFullYear(1);
        }
        return value;
      }
      if (value == null || value == "" || value == undefined)
        return null;

      if (typeof value === "string") {
        let dateFormat = getLocaleDateString();
        value = parseDate(value, dateFormat);
      }
      if (!isNaN(value))
        value = new Date(value);

      if (!fn.IsDate(value))
        fail(" Must be Date");
      return value;
    default:
      if (prop.EnumMappings) {
        if (typeof value == "string") {
          var intVal;
          if ((intVal = parseInt(value)) >= 0)
            value = intVal;
          else if (!prop.EnumMappings.some(m => { if (m.Title == value || m.Value.toString() == value) { value = m.Value; return true; } return false; }))
            fail("Invalid Enum Mapping Value!");
        }

        if (typeof value == "number")
          if (!prop.EnumMappings.some(item => item.Value == value))
            fail("Invalid Enum Value!");

        return value;
      }
      else {
        throw new Error(`TODO : Implement enforceTyped for Type ${prop.Type}!`);
      }
  }
}

export function getLocaleDateString() {
  const formats = {
    "af-ZA": "yyyy/MM/dd",
    "am-ET": "d/M/yyyy",
    "ar-AE": "dd/MM/yyyy",
    "ar-BH": "dd/MM/yyyy",
    "ar-DZ": "dd-MM-yyyy",
    "ar-EG": "dd/MM/yyyy",
    "ar-IQ": "dd/MM/yyyy",
    "ar-JO": "dd/MM/yyyy",
    "ar-KW": "dd/MM/yyyy",
    "ar-LB": "dd/MM/yyyy",
    "ar-LY": "dd/MM/yyyy",
    "ar-MA": "dd-MM-yyyy",
    "ar-OM": "dd/MM/yyyy",
    "ar-QA": "dd/MM/yyyy",
    "ar-SA": "dd/MM/yy",
    "ar-SY": "dd/MM/yyyy",
    "ar-TN": "dd-MM-yyyy",
    "ar-YE": "dd/MM/yyyy",
    "arn-CL": "dd-MM-yyyy",
    "as-IN": "dd-MM-yyyy",
    "az-Cyrl-AZ": "dd.MM.yyyy",
    "az-Latn-AZ": "dd.MM.yyyy",
    "ba-RU": "dd.MM.yy",
    "be-BY": "dd.MM.yyyy",
    "bg-BG": "dd.M.yyyy",
    "bn-BD": "dd-MM-yy",
    "bn-IN": "dd-MM-yy",
    "bo-CN": "yyyy/M/d",
    "br-FR": "dd/MM/yyyy",
    "bs-Cyrl-BA": "d.M.yyyy",
    "bs-Latn-BA": "d.M.yyyy",
    "ca-ES": "dd/MM/yyyy",
    "co-FR": "dd/MM/yyyy",
    "cs-CZ": "d.M.yyyy",
    "cy-GB": "dd/MM/yyyy",
    "da-DK": "dd-MM-yyyy",
    "de-AT": "dd.MM.yyyy",
    "de-CH": "dd.MM.yyyy",
    "de-DE": "dd.MM.yyyy",
    "de-LI": "dd.MM.yyyy",
    "de-LU": "dd.MM.yyyy",
    "dsb-DE": "d. M. yyyy",
    "dv-MV": "dd/MM/yy",
    "el-GR": "d/M/yyyy",
    "en-029": "MM/dd/yyyy",
    "en-AU": "d/MM/yyyy",
    "en-BZ": "dd/MM/yyyy",
    "en-CA": "dd/MM/yyyy",
    "en-GB": "dd/MM/yyyy",
    "en-IE": "dd/MM/yyyy",
    "en-IN": "dd-MM-yyyy",
    "en-JM": "dd/MM/yyyy",
    "en-MY": "d/M/yyyy",
    "en-NZ": "d/MM/yyyy",
    "en-PH": "M/d/yyyy",
    "en-SG": "d/M/yyyy",
    "en-TT": "dd/MM/yyyy",
    "en-US": "M/d/yyyy",
    "en-ZA": "yyyy/MM/dd",
    "en-ZW": "M/d/yyyy",
    "es-AR": "dd/MM/yyyy",
    "es-BO": "dd/MM/yyyy",
    "es-CL": "dd-MM-yyyy",
    "es-CO": "dd/MM/yyyy",
    "es-CR": "dd/MM/yyyy",
    "es-DO": "dd/MM/yyyy",
    "es-EC": "dd/MM/yyyy",
    "es-ES": "dd/MM/yyyy",
    "es-GT": "dd/MM/yyyy",
    "es-HN": "dd/MM/yyyy",
    "es-MX": "dd/MM/yyyy",
    "es-NI": "dd/MM/yyyy",
    "es-PA": "MM/dd/yyyy",
    "es-PE": "dd/MM/yyyy",
    "es-PR": "dd/MM/yyyy",
    "es-PY": "dd/MM/yyyy",
    "es-SV": "dd/MM/yyyy",
    "es-US": "M/d/yyyy",
    "es-UY": "dd/MM/yyyy",
    "es-VE": "dd/MM/yyyy",
    "et-EE": "d.MM.yyyy",
    "eu-ES": "yyyy/MM/dd",
    "fa-IR": "MM/dd/yyyy",
    "fi-FI": "d.M.yyyy",
    "fil-PH": "M/d/yyyy",
    "fo-FO": "dd-MM-yyyy",
    "fr-BE": "d/MM/yyyy",
    "fr-CA": "yyyy-MM-dd",
    "fr-CH": "dd.MM.yyyy",
    "fr-FR": "dd/MM/yyyy",
    "fr-LU": "dd/MM/yyyy",
    "fr-MC": "dd/MM/yyyy",
    "fy-NL": "d-M-yyyy",
    "ga-IE": "dd/MM/yyyy",
    "gd-GB": "dd/MM/yyyy",
    "gl-ES": "dd/MM/yy",
    "gsw-FR": "dd/MM/yyyy",
    "gu-IN": "dd-MM-yy",
    "ha-Latn-NG": "d/M/yyyy",
    "he-IL": "dd/MM/yyyy",
    "hi-IN": "dd-MM-yyyy",
    "hr-BA": "d.M.yyyy.",
    "hr-HR": "d.M.yyyy",
    "hsb-DE": "d. M. yyyy",
    "hu-HU": "yyyy. MM. dd.",
    "hy-AM": "dd.MM.yyyy",
    "id-ID": "dd/MM/yyyy",
    "ig-NG": "d/M/yyyy",
    "ii-CN": "yyyy/M/d",
    "is-IS": "d.M.yyyy",
    "it-CH": "dd.MM.yyyy",
    "it-IT": "dd/MM/yyyy",
    "iu-Cans-CA": "d/M/yyyy",
    "iu-Latn-CA": "d/MM/yyyy",
    "ja-JP": "yyyy/MM/dd",
    "ka-GE": "dd.MM.yyyy",
    "kk-KZ": "dd.MM.yyyy",
    "kl-GL": "dd-MM-yyyy",
    "km-KH": "yyyy-MM-dd",
    "kn-IN": "dd-MM-yy",
    "ko-KR": "yyyy. MM. dd",
    "kok-IN": "dd-MM-yyyy",
    "ky-KG": "dd.MM.yy",
    "lb-LU": "dd/MM/yyyy",
    "lo-LA": "dd/MM/yyyy",
    "lt-LT": "yyyy.MM.dd",
    "lv-LV": "yyyy.MM.dd.",
    "mi-NZ": "dd/MM/yyyy",
    "mk-MK": "dd.MM.yyyy",
    "ml-IN": "dd-MM-yy",
    "mn-MN": "yy.MM.dd",
    "mn-Mong-CN": "yyyy/M/d",
    "moh-CA": "M/d/yyyy",
    "mr-IN": "dd-MM-yyyy",
    "ms-BN": "dd/MM/yyyy",
    "ms-MY": "dd/MM/yyyy",
    "mt-MT": "dd/MM/yyyy",
    "nb-NO": "dd.MM.yyyy",
    "ne-NP": "M/d/yyyy",
    "nl-BE": "d/MM/yyyy",
    "nl-NL": "d-M-yyyy",
    "nn-NO": "dd.MM.yyyy",
    "nso-ZA": "yyyy/MM/dd",
    "oc-FR": "dd/MM/yyyy",
    "or-IN": "dd-MM-yy",
    "pa-IN": "dd-MM-yy",
    "pl-PL": "dd.MM.yyyy",
    "prs-AF": "dd/MM/yy",
    "ps-AF": "dd/MM/yy",
    "pt-BR": "d/M/yyyy",
    "pt-PT": "dd-MM-yyyy",
    "qut-GT": "dd/MM/yyyy",
    "quz-BO": "dd/MM/yyyy",
    "quz-EC": "dd/MM/yyyy",
    "quz-PE": "dd/MM/yyyy",
    "rm-CH": "dd/MM/yyyy",
    "ro-RO": "dd.MM.yyyy",
    "ru-RU": "dd.MM.yyyy",
    "rw-RW": "M/d/yyyy",
    "sa-IN": "dd-MM-yyyy",
    "sah-RU": "MM.dd.yyyy",
    "se-FI": "d.M.yyyy",
    "se-NO": "dd.MM.yyyy",
    "se-SE": "yyyy-MM-dd",
    "si-LK": "yyyy-MM-dd",
    "sk-SK": "d. M. yyyy",
    "sl-SI": "d.M.yyyy",
    "sma-NO": "dd.MM.yyyy",
    "sma-SE": "yyyy-MM-dd",
    "smj-NO": "dd.MM.yyyy",
    "smj-SE": "yyyy-MM-dd",
    "smn-FI": "d.M.yyyy",
    "sms-FI": "d.M.yyyy",
    "sq-AL": "yyyy-MM-dd",
    "sr-Cyrl-BA": "d.M.yyyy",
    "sr-Cyrl-CS": "d.M.yyyy",
    "sr-Cyrl-ME": "d.M.yyyy",
    "sr-Cyrl-RS": "d.M.yyyy",
    "sr-Latn-BA": "d.M.yyyy",
    "sr-Latn-CS": "d.M.yyyy",
    "sr-Latn-ME": "d.M.yyyy",
    "sr-Latn-RS": "d.M.yyyy",
    "sv-FI": "d.M.yyyy",
    "sv-SE": "yyyy-MM-dd",
    "sw-KE": "M/d/yyyy",
    "syr-SY": "dd/MM/yyyy",
    "ta-IN": "dd-MM-yyyy",
    "te-IN": "dd-MM-yy",
    "tg-Cyrl-TJ": "dd.MM.yy",
    "th-TH": "d/M/yyyy",
    "tk-TM": "dd.MM.yy",
    "tn-ZA": "yyyy/MM/dd",
    "tr-TR": "dd.MM.yyyy",
    "tt-RU": "dd.MM.yyyy",
    "tzm-Latn-DZ": "dd-MM-yyyy",
    "ug-CN": "yyyy-M-d",
    "uk-UA": "dd.MM.yyyy",
    "ur-PK": "dd/MM/yyyy",
    "uz-Cyrl-UZ": "dd.MM.yyyy",
    "uz-Latn-UZ": "dd/MM yyyy",
    "vi-VN": "dd/MM/yyyy",
    "wo-SN": "dd/MM/yyyy",
    "xh-ZA": "yyyy/MM/dd",
    "yo-NG": "d/M/yyyy",
    "zh-CN": "yyyy/M/d",
    "zh-HK": "d/M/yyyy",
    "zh-MO": "d/M/yyyy",
    "zh-SG": "d/M/yyyy",
    "zh-TW": "yyyy/M/d",
    "zu-ZA": "yyyy/MM/dd",
  };

  return formats[navigator.language] || "dd/MM/yyyy";
}

export function parseDate(value, format) {
  if (value.length != format.length)
    return fn.ToDate(value);

  const formatParts = format.split(/[-/.]/);
  const valueParts = value.split(/[-/.]/);

  let day, month, year;

  formatParts.forEach((part, index) => {
    if (part.includes('d')) {
      day = parseInt(valueParts[index], 10);
    } else if (part.includes('M')) {
      month = parseInt(valueParts[index], 10) - 1; 
    } else if (part.includes('y')) {
      year = parseInt(valueParts[index], 10);
    }
  });

  if (day !== undefined && month !== undefined && year !== undefined) {
    return new Date(year, month, day);
  }

  return fn.ToDate(value);
}



